import { SimpleCrypto } from "simple-crypto-js";
import { i18n } from "@/i18n";
import { showPrompt } from "@core/ui/alert/alert";
import { compress, decompress } from "./Compressor";

const secretKey = "168DA1E7AF1FCD868FD7625BA5FCD";

class Encrytor {
  currentPassword: string | undefined;

  constructor() {
    this.currentPassword = undefined;
  }

  /**
   * Try to decrypt an encrypted string
   */
  decrypt(encryptedData: string, password = secretKey) {
    try {
      const data = this.getCrypto(password).decrypt(encryptedData);
      if (typeof data === "string") {
        try {
          // Save states starting from 3.7.4 contain compressed data,
          // so we need to decompress it after decryption.
          // If this fails, we might deal with uncompressed data from saves of older versions.
          const decompressed = decompress(data);
          return JSON.parse(decompressed);
        } catch (error) {
          console.error("Decompression failed", error);
          return JSON.parse(data);
        }
      }
      return data;
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  /**
   * Creates an encrypted text out of any data
   */
  encrypt(data: string | number | boolean | object, password?: string) {
    // empty password counts as no password.
    if (password === "") password = undefined;

    // No password passed? Use the standard password.
    if (password == null) {
      password = secretKey;
    }
    const dataString = JSON.stringify(data);
    const compressed = compress(dataString);
    return this.getCrypto(password).encrypt(compressed);
  }

  /**
   * Try decoding encrypted data with standard password first, then ask for password.
   */
  async decryptAskingForPassword(encryptedData: string) {
    let data = await this.decrypt(encryptedData);

    if (data !== false) {
      this.password = undefined;
      return data;
    }

    // data could not be decoded. ask for password.
    // keep asking until we get correct data or give up.
    while (data === false) {
      // prompt for password
      const inputData = await showPrompt<{ password: string } | false>({
        title: i18n.t("labels.passwordProtection"),
        content: [
          {
            type: "text",
            text: i18n.t("messages.passwordProtected")
          },
          {
            type: "password",
            name: "password",
            autocomplete: "new-password",
            user: "model",
            current: true,
            value: "",
            label: i18n.t("labels.password")
          }
        ]
      });

      // cancelled? abort.
      if (inputData === false) {
        this.password = undefined;
        return false;
      }

      data = await this.decrypt(encryptedData, inputData.password);
      this.password = inputData.password;
    }
    return data;
  }

  getCrypto(password?: string) {
    try {
      if (password == null) {
        return new SimpleCrypto(secretKey);
      }
      return new SimpleCrypto(password);
    } catch (error) {
      console.error("Error creating SimpleCrypto instance", error);
      console.log("SimpleCrypto:", SimpleCrypto);
      throw error;
    }
  }

  /**
   * Sets the password to null
   */
  reset() {
    this.password = undefined;
  }

  // GETTERS / SETTERS

  get password() {
    return this.currentPassword;
  }
  set password(value) {
    this.currentPassword = value;
  }
}

const encryptor = new Encrytor();
export default encryptor;
